/**
 * DerivedDietFlagsRow renders the diets a recipe's ingredients rule out.
 *
 * An allergen tag proves a food contains something; a missing tag proves
 * nothing, because most foods are not fully tagged. So a diet claim can be
 * ruled out by a tag but never confirmed by the absence of one: this row only
 * ever shows "Not vegan (Milk)", never a green tick.
 *
 * Read-only, system-derived. The frontend does not recompute the diet rules;
 * it only renders the resolved state returned by the backend.
 */
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

export type DietFlagKey =
  | "vegetarian"
  | "vegan"
  | "dairyFree"
  | "glutenFree"
  | "eggFree"
  | "jain"
  | "satvik";

export type DietFlagStatus = "fail" | "unknown";

export interface DietFlagState {
  status: DietFlagStatus;
  /** Display names of what breaks the claim (ingredients, hand-added tags). */
  brokenBy?: string[];
}

/** How a broken claim reads: "Not vegan", "Contains dairy". */
export const DIET_FLAG_RULED_OUT_LABELS: Record<DietFlagKey, string> = {
  vegetarian: "Not vegetarian",
  vegan: "Not vegan",
  dairyFree: "Contains dairy",
  glutenFree: "Contains gluten",
  eggFree: "Contains egg",
  jain: "Not Jain",
  satvik: "Not Satvik",
};

export const DIET_FLAG_ORDER: DietFlagKey[] = [
  "vegetarian",
  "vegan",
  "dairyFree",
  "glutenFree",
  "eggFree",
  "jain",
  "satvik",
];

interface DietFlagChipProps {
  label: string;
  state: DietFlagState;
  /** Show what breaks the claim inline (recipe level). */
  showNames?: boolean;
}

export const DietFlagChip = ({
  label,
  state,
  showNames,
}: DietFlagChipProps) => {
  const names =
    state.brokenBy && state.brokenBy.length > 0
      ? state.brokenBy.join(", ")
      : undefined;
  return (
    <span
      title={names ? `${label}: ${names}` : label}
      className="inline-flex min-h-6 items-center gap-1 rounded-full border border-destructive/35 bg-destructive/10 px-2.5 py-0.5 z-hint font-medium text-destructive"
    >
      <X className="size-3" aria-hidden />
      <span>{label}</span>
      {showNames && names && (
        <span className="font-normal opacity-80">({names})</span>
      )}
    </span>
  );
};

interface DerivedDietFlagsRowProps {
  states: Record<DietFlagKey, DietFlagState>;
  /** Shown when no ingredients have been added yet. */
  isEmpty?: boolean;
  className?: string;
}

export const DerivedDietFlagsRow = ({
  states,
  isEmpty,
  className,
}: DerivedDietFlagsRowProps) => {
  if (isEmpty) {
    return (
      <p className={cn("z-caption text-muted-foreground/70", className)}>
        Add ingredients to see which diets they rule out.
      </p>
    );
  }

  const ruledOut = DIET_FLAG_ORDER.filter(
    (key) => states[key].status === "fail"
  );

  return (
    <div className={cn("space-y-1.5", className)}>
      {ruledOut.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {ruledOut.map((key) => (
            <DietFlagChip
              key={key}
              label={DIET_FLAG_RULED_OUT_LABELS[key]}
              state={states[key]}
              showNames
            />
          ))}
        </div>
      )}
      <p className="z-caption text-muted-foreground">
        {ruledOut.length > 0
          ? "Worked out from the ingredients' allergen tags. Diets not listed aren't confirmed — an ingredient may simply not be tagged."
          : "No ingredient's allergen tags rule out a diet. That isn't confirmation — an ingredient may simply not be tagged."}
      </p>
    </div>
  );
};

export default DerivedDietFlagsRow;
